import { app } from "@microsoft/teams-js";
import { TeamsSurveyDraft, TeamsUser } from "@teams/types";
import { ScheduleDTO, SurveyDTO, SurveyUpdateOptionsDTO } from "@teams/types/api";

import {
  FetchResult,
  deserializeSurveyDTO,
  encodeApiUrl,
  fetchApiWithContext,
  getGroupIdFromContext,
  isSuccess,
} from "./ChannelApi";
import { ChannelSurveysOptions, getAudienceIdFromContext } from "./SurveyApi";
import { SURVEY_STATIC_TABS } from "../constants/StaticTabEnum";

export type PaginatedSurveysOptions = {
  skip: number;
  limit: number;
  includeResponses?: boolean;
};

export const scheduleApi = {
  getSeriesById: async (
    context: app.Context,
    seriesId: string,
    groupId?: string | null,
    includeLastSurvey?: boolean
  ): Promise<FetchResult<ScheduleDTO> | undefined> => {

    if (!groupId && context.page.id !== SURVEY_STATIC_TABS.DASHBOARD ) {
      groupId = getGroupIdFromContext(context);
    }

    const params = new URLSearchParams();
    if (includeLastSurvey) {
      params.append("includeLastSurvey", "true");
    }
    let schedule: FetchResult<ScheduleDTO>;
    if (groupId) {
      schedule = await fetchApiWithContext(
        context,
        encodeApiUrl`/v1/team/${groupId}/series/${seriesId}`,
        {
          queryParams: params,
        }
      );
    } else {
      schedule = await fetchApiWithContext(context, encodeApiUrl`/v1/series/${seriesId}`, {
        queryParams: params,
      });
    }

    if (isSuccess(schedule)) {
      deserializeScheduleDTO(schedule);
    }
    return schedule;
  },

  audienceSchedules: async (
    context: app.Context,
    options: ChannelSurveysOptions
  ): Promise<FetchResult<ScheduleDTO[]>> => {
    const groupId = getGroupIdFromContext(context);
    const audienceId = getAudienceIdFromContext(context);

    if (groupId === null || audienceId === null) {
      console.error(
        "Unable to load surveys due to missing groupId or audienceId",
        context,
        groupId,
        audienceId
      );
      return [];
    }

    const params = new URLSearchParams();
    if (options.includeResponses) {
      params.append("includeResponses", "true");
    }

    const schedules: FetchResult<ScheduleDTO[]> = await fetchApiWithContext(
      context,
      encodeApiUrl`/v1/team/${groupId}/audience/${audienceId}/series`,
      {
        queryParams: params,
      }
    );

    if (isSuccess(schedules)) {
      schedules.forEach(deserializeScheduleDTO);
    }
    return schedules;
  },

  userSchedules: async (
    context: app.Context,
    options: ChannelSurveysOptions
  ): Promise<FetchResult<ScheduleDTO[]>> => {
    const params = new URLSearchParams();
    if (options.includeResponses) {
      params.append("includeResponses", "true");
    }

    const schedules: FetchResult<ScheduleDTO[]> = await fetchApiWithContext(
      context,
      encodeApiUrl`/v1/owner/series`,
      {
        queryParams: params,
      }
    );

    if (isSuccess(schedules)) {
      schedules.forEach(deserializeScheduleDTO);
    }
    return schedules;
  },

  userSchedulesByAudience: async (
    context: app.Context,
    options: ChannelSurveysOptions
  ): Promise<FetchResult<ScheduleDTO[]>> => {
    const audienceId = getAudienceIdFromContext(context);
    if (audienceId === null) {
      console.error("Unable to load schedules due to missing audienceId", context);
      return [];
    }

    const params = new URLSearchParams();
    if (options.includeResponses) {
      params.append("includeResponses", "true");
    }

    const schedules: FetchResult<ScheduleDTO[]> = await fetchApiWithContext(
      context,
      encodeApiUrl`/v1/audience/${audienceId}/series`,
      {
        queryParams: params,
      }
    );

    if (isSuccess(schedules)) {
      schedules.forEach(deserializeScheduleDTO);
    }
    return schedules;
  },

  getHistoricalSurveys: async (
    context: app.Context,
    seriesId: string,
    options: PaginatedSurveysOptions,
    groupId?: string | null
  ): Promise<
    FetchResult<{
      surveys: SurveyDTO[];
      total: number;
    }>
  > => {
    if (!groupId) {
      groupId = getGroupIdFromContext(context);
    }

    if (groupId === null) {
      console.error("Unable to get series due to missing groupId", context, groupId);
      return { surveys: [], total: 0 };
    }

    const params = new URLSearchParams();
    params.append("skip", options.skip.toString());
    params.append("limit", options.limit.toString());
    if (options.includeResponses) {
      params.append("includeResponses", "true");
    }

    const historicalSurveys: FetchResult<{
      surveys: SurveyDTO[];
      total: number;
    }> = await fetchApiWithContext(
      context,
      encodeApiUrl`/v1/team/${groupId}/series/${seriesId}/historicalSurveys`,
      {
        queryParams: params,
      }
    );

    if (isSuccess(historicalSurveys)) {
      historicalSurveys.surveys.forEach(deserializeSurveyDTO);
    }

    return historicalSurveys;
  },

  exportSeriesCsv: async (context: app.Context, scheduleId: string) => {
    return await fetchApiWithContext<string>(
      context,
      encodeApiUrl`/v1/series/${scheduleId}/export`,
      undefined,
      true
    );
  },

  addOwner: async (
    context: app.Context,
    scheduleId: string,
    user: TeamsUser
  ): Promise<FetchResult<void>> => {
    const update = await fetchApiWithContext<TeamsUser>(
      context,
      encodeApiUrl`/v1/series/${scheduleId}/owner`,
      { method: "post", body: JSON.stringify(user) }
    );

    if (isSuccess(update)) {
      return;
    }

    return update;
  },

  removeOwner: async (
    context: app.Context,
    scheduleId: string,
    userAADId: string
  ): Promise<FetchResult<void>> => {
    const update = await fetchApiWithContext<TeamsUser>(
      context,
      encodeApiUrl`/v1/series/${scheduleId}/owner/${userAADId}`,
      { method: "delete" }
    );

    if (isSuccess(update)) {
      return;
    }

    return update;
  },

  update: async (
    context: app.Context,
    scheduleId: string,
    draft: TeamsSurveyDraft,
    updateOptions?: SurveyUpdateOptionsDTO
  ) => {
    return fetchApiWithContext(context, encodeApiUrl`/v1/series/${scheduleId}`, {
      method: "PUT",
      body: JSON.stringify({ draft, updateOptions }),
    });
  },
};

function deserializeScheduleDTO(schedule: ScheduleDTO) {
  schedule.next = new Date(schedule.next);
  schedule.updatedAt = new Date(schedule.updatedAt);
  if (schedule.lastSurvey) {
    deserializeSurveyDTO(schedule.lastSurvey);
  }
}
